/* eslint-disable react/prop-types */
import React from "react";
import { Box, Typography, makeStyles, useMediaQuery } from "@material-ui/core";
import _ from "lodash";
import moment from "moment";
import clsx from "clsx";
import { useSelector } from "react-redux";
import { RiMastercardLine } from "react-icons/ri";

const borderWidth = 2;
const useStyles = makeStyles((theme) => ({
  customTooltipContainer: {
    border: "1px solid #e2e2e2",
    overflow: "hidden",
    padding: "10px",
    borderRadius: "3px",
    display: "flex",
    flexFlow: "row",
    backgroundColor: "#fff",
    boxShadow: "0 1px 10px 0 rgba(9,42,62,0.18)",
    opacity: "0.9",
    pointerEvents: "none",
    zIndex: "9999",
    marginBottom: "10px",
    "& *": {
      overflow: "hidden",
    },
  },
  smallPadding: {
    padding: "5px",
  },
  verticalDivider: {
    border: `${borderWidth}px solid ${theme.palette.primary.main}`,
    borderRadius: "3px",
    marginRight: "10px",
    width: "2px",
  },
  itemContainer: {
    display: "flex",
    "& p": {
      overflow: "hidden",
      "-webkit-line-clamp": "2",
      "-webkit-box-orient": "vertical",
      textOverflow: "ellipsis",
      wordWrap: "break-word",
    },
  },
  icon: {
    width: "20px",
    height: "20px",
    color: "#999999",
  },
  multibuyTextWithIcon: {
    display: "flex",
    alignItems: "center",
  },
  tooltipTitle: {
    color: "#181E2A",
    display: "flex",
    marginBottom: "10px",
    // justifyContent: "center",
  },
  tooltipSubtitle: {
    fontSize: "0.75rem",
  },
  roiContainer: {
    top: "auto",
    bottom: 0,
    textAlign: "center",
  },
  isPriceHoverContainer: {
    textAlign: "center",
    paddingBottom: "5px",
    color: "#a2a2a2",
  },
  multibuyContainer: {
    marginBottom: "10px",
  },
}));

const CustomTooltip = ({
  tooltipData,
  tooltipProps,
  body,
  items,
  roi,
  isPriceHover,
  deltaEvent,
  metadataUnit,
  // promPrice,
  // referenceRowPrice,
  hoverPrice,
  selectedPrice,
  eventLabelHoverChart,
  scenarioEstimationEventPerDate,
  elasticity,
  fixedWidth = true,
}) => {
  const classes = useStyles();
  const ref = React.useRef();

  const [position, setPosition] = React.useState();

  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down("md"));

  const [dimension, setDimension] = React.useState({
    width:
      tooltipProps.isDemandPlanning && isSmallScreen
        ? 100
        : tooltipProps.width || 180,
    maxWidth:
      tooltipProps.isDemandPlanning && isSmallScreen
        ? 100
        : tooltipProps.width || 180,
  });
  const paddingThreshold = isSmallScreen ? 10 : 20;

  const cycleTypeId = useSelector(
    (state) => state.scenarios.selectedScenario.scenario.cycle.cycle_type_id
  );

  const getPositionWithinWindow = (currentPosition, size, windowLimit) => {
    let offset = 0;
    let newPosition = currentPosition;

    // If the currentPosition is out of boundaries
    if (newPosition < 0) newPosition = paddingThreshold;
    if (newPosition > windowLimit) newPosition = windowLimit - paddingThreshold;

    const distanceToWindowLimit =
      windowLimit - paddingThreshold - (newPosition + size);
    // If the position + size exceeds the window size (width or height) limit, then
    // displace the position towards the center of the window
    if (distanceToWindowLimit <= 0)
      offset -= Math.abs(distanceToWindowLimit) + paddingThreshold;

    return newPosition + offset;
  };

  const getWidthDimension = (tooltipWidth, widthLimit) => {
    // We add borderWidth as ref.current.clientWidth does not take border width into account
    let newWidth = tooltipWidth + borderWidth;
    const displayableWidth = widthLimit - 2 * paddingThreshold;

    if (newWidth >= displayableWidth) newWidth = displayableWidth;

    return newWidth;
  };

  React.useEffect(() => {
    if (ref.current) {
      const tooltipWidth = getWidthDimension(
        ref.current.clientWidth,
        window.innerWidth
      );
      setDimension({
        width:
          tooltipProps.isDemandPlanning && isSmallScreen ? 200 : tooltipWidth,
        maxWidth:
          tooltipProps.isDemandPlanning && isSmallScreen ? 200 : tooltipWidth,
      });

      setPosition({
        top: getPositionWithinWindow(
          tooltipProps.top,
          ref.current.clientHeight,
          window.innerHeight
        ),
        left: getPositionWithinWindow(
          tooltipProps.left,
          tooltipWidth,
          window.innerWidth
        ),
      });
    }
  }, [tooltipProps, ref]);

  // promPrice and deltaEvent.promPrice is the same but we need always promPrice without "NA"

  // const checkPriceForRedemption =
  //   promPrice >= hoverPrice && hoverPrice < referenceRowPrice
  //     ? 100
  //     : deltaEvent?.redemption;
  // const checkPriceForPromPrice =
  //   promPrice >= hoverPrice && hoverPrice < referenceRowPrice
  //     ? hoverPrice
  //     : deltaEvent?.promPrice;
  // const checkRedemption =
  //   deltaEvent?.redemption <= 0 ? 0 : checkPriceForRedemption;

  const checkPriceForPromPrice = deltaEvent?.promPrice;
  const checkRedemption = deltaEvent?.redemption;

  return tooltipData ? (
    <div
      ref={ref}
      className={clsx({
        [classes.customTooltipContainer]:
          tooltipProps.smallPadding !== true || tooltipProps.smallPadding,
        [tooltipProps.tooltipModel.yAlign]:
          tooltipProps.smallPadding !== true || tooltipProps.smallPadding,
        [classes.smallPadding]: tooltipProps.smallPadding,
      })}
      // className={`${classes.customTooltipContainer} ${tooltipProps.tooltipModel.yAlign} ${classes.smallPadding}`}
      style={{
        visibility: !position && "hidden",
        top: position?.top || tooltipProps.top,
        left: position?.left || tooltipProps.left,
        position: tooltipProps.position,
        width: fixedWidth ? dimension?.width : "auto",
        maxWidth: fixedWidth ? dimension?.maxWidth : "auto",
      }}
    >
      {tooltipData.borderStyle && (
        <Box
          component="div"
          className={classes.verticalDivider}
          style={tooltipData.borderStyle}
        />
      )}
      <Box component="div" style={{ width: "100%" }}>
        {tooltipData.title && (
          <Box component="div" className={`${classes.tooltipTitle}`}>
            {tooltipData.title}
            {scenarioEstimationEventPerDate &&
              ` - ${
                scenarioEstimationEventPerDate[
                  moment(tooltipData.title).format("YYYY-MM-DD")
                ]?.label
              }`}
          </Box>
        )}

        {isPriceHover && (
          <>
            <Typography
              variant="body2"
              className={classes.isPriceHoverContainer}
            >
              This price point is an average of different prices at lower
              aggregation levels.
            </Typography>
            <Typography
              variant="body2"
              className={classes.isPriceHoverContainer}
            >
              It can’t be selected.
            </Typography>
          </>
        )}

        {hoverPrice === selectedPrice ? (
          <>
            {cycleTypeId === 4 &&
              deltaEvent?.id === null &&
              eventLabelHoverChart}
          </>
        ) : (
          <>
            {cycleTypeId === 4 && deltaEvent?.id === null && (
              <Box component="div" className={`${classes.tooltipTitle}`}>
                TPR
              </Box>
            )}
          </>
        )}

        {hoverPrice === selectedPrice ? (
          <>
            {cycleTypeId === 4 && deltaEvent?.id === 1 && (
              <>
                {eventLabelHoverChart}
                <Typography
                  variant="body2"
                  className={classes.multibuyContainer}
                >
                  {!(
                    Number.isNaN(deltaEvent?.promPrice) &&
                    Number.isNaN(checkPriceForPromPrice)
                  ) && (
                    <div>
                      MB Price:{" "}
                      {deltaEvent?.promPrice === "NA" ? null : metadataUnit}{" "}
                      {checkPriceForPromPrice}
                    </div>
                  )}
                  {!(
                    Number.isNaN(deltaEvent?.redemption) &&
                    Number.isNaN(checkRedemption)
                  ) && (
                    <div>
                      Redemption:{" "}
                      {deltaEvent?.redemption >= 100 ? 100 : checkRedemption}%
                    </div>
                  )}
                </Typography>
              </>
            )}
          </>
        ) : (
          <>
            {cycleTypeId === 4 && deltaEvent?.id === 1 && (
              <>
                <Box component="div" className={`${classes.tooltipTitle}`}>
                  <div className={`${classes.multibuyTextWithIcon}`}>
                    Multibuy&ensp;
                    <RiMastercardLine className={`${classes.icon}`} />
                  </div>
                </Box>
                <Typography
                  variant="body2"
                  className={classes.multibuyContainer}
                >
                  {!(
                    Number.isNaN(deltaEvent?.promPrice) &&
                    Number.isNaN(checkPriceForPromPrice)
                  ) && (
                    <div>
                      MB Price:{" "}
                      {deltaEvent?.promPrice === "NA" ? null : metadataUnit}{" "}
                      {checkPriceForPromPrice}
                    </div>
                  )}
                  {!(
                    Number.isNaN(deltaEvent?.redemption) &&
                    Number.isNaN(checkRedemption)
                  ) && (
                    <div>
                      Redemption:{" "}
                      {deltaEvent?.redemption >= 100 ? 100 : checkRedemption}%
                    </div>
                  )}
                </Typography>
              </>
            )}
          </>
        )}

        {roi !== null && roi !== undefined && (
          <Typography className={classes.roiContainer}>ROI {roi}</Typography>
        )}

        {elasticity !== undefined && <div>Elasticity: {elasticity}</div>}

        {tooltipData.subtitle && (
          <Typography variant="body2" className={classes.tooltipSubtitle}>
            <i>{tooltipData.subtitle}</i>
          </Typography>
        )}
        {_.map(items, (item, i) => {
          return item ? (
            <Box
              component="div"
              key={i}
              className={classes.itemContainer}
              style={item.containerStyle || { marginTop: "5px" }}
            >
              <Box
                component="div"
                className={classes.verticalDivider}
                style={item.borderStyle}
              />
              {item.content}
            </Box>
          ) : null;
        })}
        {body}
      </Box>
    </div>
  ) : null;
};

export default CustomTooltip;
